import React, { memo, useEffect, useState, useRef } from "react";
import * as S from "./styles";
import _ from "lodash";
import OpenImage from "./OpenImage";
import "react-awesome-lightbox/build/style.css";
import { MessageItem } from "./MessageItem";
import { useMemo } from "react";
import { useParams } from "react-router";
import { useContacts, useContactsState } from "modules/app/contexts";
import { PasteImageModal } from "./PasteImageModal";
import { pasteImgListener } from "modules/app/utils/models/pasteImgListener";
import { getMessages } from "modules/app/utils/http";
import moment from "moment";
import { toast } from "react-toastify";

const TOTAL_MESSAGES_PER_PAGE = 10;

function Messages({
  setAnswerMessage,
  customer,
  handleCloseEmojiPicker,
  upMessage,
  isLastMsgVisible,
}) {
  const { loadingContact, currentContact } = useContactsState();
  const { updateCurrentContact } = useContacts();
  const { messages, totalPages } = currentContact;
  const [showImage, setShowImage] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [pasteImgModal, setPasteImgModal] = useState(false);
  const [imgSrc, setImgSrc] = useState();
  const [imgBlob, setImgBlob] = useState();

  const messagesSentinelRef = useRef(null);
  const lastMessageRef = useRef(null);
  const messagesRef = useRef(null);

  const { id } = useParams();

  const [page, setPage] = useState(1);

  function focusMessage(message) {
    setTimeout(() => {
      const messagesElement = window.document.getElementById("messages");

      const messageElementBody = document.getElementById(
        `message-body-${message?.id}`
      );

      if (messagesElement && messageElementBody) {
        messageElementBody.classList.add("highlight");

        setTimeout(() => {
          messageElementBody.classList.remove("highlight");
        }, 1000);
      }
    }, 0);
  }

  async function loadMoreMessages(page) {
    try {
      const { data } = await getMessages(currentContact?.id, page);
      const sortedMessages = data.reverse();

      const newMessages = [...sortedMessages, ...currentContact.messages];
      updateCurrentContact({
        ...currentContact,
        messages: _.uniqBy(newMessages, "id"),
        page: currentContact.page + 1,
      });
    } catch (error) {
      toast.error(t("Chat:labels.load_msg_error"));
    }

    setLoadingMessages(false);
  }

  function handlePasteImg(e) {
    const results = pasteImgListener(e);
    if (results) {
      setImgSrc(results?.imgSrc);
      setImgBlob(results?.blob);
      setPasteImgModal(true);
    }
  }

  const imagesUrls = useMemo(
    () =>
      messages
        ?.filter((message) =>
          message?.attachment_content_type?.includes("image")
        )
        ?.map((message) => message.attachment_url),
    [messages]
  );

  const currentPage = useMemo(() => {
    return page;
  }, [page]);

  const allMessages = useMemo(() => {
    return messages;
  }, [messages]);

  const handleScroll = (entries) => {
    const [entry] = entries;

    if (entry.isIntersecting) {
      setLoadingMessages(true);
      setTimeout(() => {
        messagesRef?.current?.scrollTo(60, 60);
      }, 1000);
      setPage((prevState) => prevState + 1);
    }
  };

  function scrollToLastMessage() {
    lastMessageRef?.current?.scrollIntoView({
      behavior: "smooth",
    });
  }

  function scrollAfterLastMessage() {
    lastMessageRef?.current?.scrollIntoView({
      behavior: "smooth",
      lock: "end",
    });
  }

  useEffect(() => {
    if (currentContact?.resetPage) {
      setPage(1);
      updateCurrentContact({
        ...currentContact,
        resetPage: false,
      });
    }
  }, [currentContact]);

  useEffect(() => {
    setPage(1);
  }, [id]);

  useEffect(() => {
    setTimeout(() => {
      scrollToLastMessage();
    }, 1500);
  }, [id]);

  useEffect(() => {
    if (isLastMsgVisible) {
      scrollAfterLastMessage();
    }
  }, [isLastMsgVisible]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleScroll, {
      root: null,
      rootMargin: "0px",
      threshold: 1.0,
    });

    setTimeout(() => {
      if (messagesSentinelRef.current) {
        observer.observe(messagesSentinelRef.current);
      }
    }, 2000);

    return () => {
      if (messagesSentinelRef.current) {
        observer.unobserve(messagesSentinelRef.current);
      }
    };
  }, [messagesSentinelRef, id]);

  useEffect(() => {
    if (page !== 1 && page <= currentContact?.totalPages) {
      loadMoreMessages(page);
    } else {
      setLoadingMessages(false);
    }
  }, [currentPage]);

  useEffect(
    function () {
      setTimeout(function () {
        const messagesElement = document.getElementById("messages");

        if (!messagesElement) {
          return;
        }

        const positionTop =
          (messagesElement.scrollTop - messagesElement.scrollHeight) * -1;

        if (positionTop < 1200) {
          messagesElement.scrollTop = messagesElement.scrollHeight;
        }
      }, 150);
    },
    [upMessage]
  );

  return (
    <S.ContainerList
      $upMessage={upMessage}
      id="messages"
      ref={messagesRef}
      onPaste={(e) => handlePasteImg(e)}
      onClick={handleCloseEmojiPicker}
    >
      {showImage && (
        <OpenImage
          messages={imagesUrls}
          setShowImage={setShowImage}
          showImage={showImage}
        />
      )}

      {loadingContact ? (
        <S.CircularLoading size={30} />
      ) : (
        <S.MessagesContainer>
          <S.MessagesHeader>
            {page > currentContact?.totalPages ||
            allMessages.length < TOTAL_MESSAGES_PER_PAGE ? (
              <div />
            ) : (
              <S.MessagesSentinel ref={messagesSentinelRef} />
            )}
            {loadingMessages && <S.MessagesLoading size={24} />}
          </S.MessagesHeader>
          {allMessages?.map(
            (message) =>
              message && (
                <MessageItem
                  focusMessage={focusMessage}
                  customer={customer}
                  setAnswerMessage={setAnswerMessage}
                  key={message.id}
                  message={message}
                  setShowImage={setShowImage}
                />
              )
          )}
          <S.MessagesFooter ref={lastMessageRef} />
        </S.MessagesContainer>
      )}
      {pasteImgModal && (
        <PasteImageModal
          imgSrc={imgSrc}
          handleCloseModal={() => setPasteImgModal(false)}
          blob={imgBlob}
        />
      )}
    </S.ContainerList>
  );
}

export default memo(Messages);
